import type { AnyEvent } from "../domain/index.js";
import { listEventsSince } from "./api.js";
import { eventStreamUrl } from "./api.js";

export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

export interface EventStreamOptions {
  projectId: string;
  initialLastGlobalSeq: number;
  onEnvelope: (env: AnyEvent) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Override for tests; defaults to the global EventSource. */
  eventSourceCtor?: typeof EventSource;
  /** Override for tests; defaults to setTimeout/clearTimeout. */
  scheduler?: { setTimeout: typeof setTimeout; clearTimeout: typeof clearTimeout };
  /** Capped exponential backoff schedule in ms. Default: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ... */
  backoffMs?: number[];
}

export interface EventStreamHandle {
  unsubscribe(): void;
  getLastGlobalSeq(): number;
}

type EnvelopeWithSeq = AnyEvent & { globalSeq: number };

const DEFAULT_BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];

export function createEventStream(opts: EventStreamOptions): EventStreamHandle {
  const Ctor = opts.eventSourceCtor ?? EventSource;
  // Bind to globalThis: bare `setTimeout`/`clearTimeout` stored on an object
  // lose their receiver, and the browser throws "Illegal invocation" when they
  // are later invoked as `sched.setTimeout(...)`.
  const sched = opts.scheduler ?? {
    setTimeout: globalThis.setTimeout.bind(globalThis),
    clearTimeout: globalThis.clearTimeout.bind(globalThis),
  };
  const backoff = opts.backoffMs ?? DEFAULT_BACKOFF;
  let lastSeq = opts.initialLastGlobalSeq;
  let attempt = 0;
  let stopped = false;
  let es: EventSource | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function deliver(env: AnyEvent): void {
    const seq = (env as unknown as EnvelopeWithSeq).globalSeq;
    if (typeof seq === "number" && seq <= lastSeq) return; // dedup
    opts.onEnvelope(env);
    if (typeof seq === "number" && seq > lastSeq) lastSeq = seq;
  }

  async function recoverGap(): Promise<void> {
    try {
      const { events } = await listEventsSince(opts.projectId, lastSeq);
      for (const ev of events) deliver(ev);
    } catch {
      // swallow — next reconnect will retry
    }
  }

  function connect(): void {
    if (stopped) return;
    opts.onStatus(attempt === 0 ? "connecting" : "reconnecting");
    es = new Ctor(eventStreamUrl(opts.projectId));
    es.onopen = (): void => {
      attempt = 0;
      opts.onStatus("connected");
      // Fire-and-forget gap recovery after (re)connect
      void recoverGap();
    };
    es.onmessage = (ev: MessageEvent<string>): void => {
      try {
        const env = JSON.parse(ev.data) as AnyEvent;
        deliver(env);
      } catch {
        // ignore malformed lines
      }
    };
    es.onerror = (): void => {
      if (stopped) return;
      try {
        es?.close();
      } catch {
        // ignore
      }
      es = null;
      const delay = backoff[Math.min(attempt, backoff.length - 1)] ?? 30000;
      attempt += 1;
      opts.onStatus("reconnecting");
      timer = sched.setTimeout(connect, delay);
    };
  }

  connect();

  return {
    unsubscribe(): void {
      stopped = true;
      opts.onStatus("disconnected");
      if (timer !== null) sched.clearTimeout(timer);
      try {
        es?.close();
      } catch {
        // ignore
      }
      es = null;
    },
    getLastGlobalSeq: () => lastSeq,
  };
}
